import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminOrganisation } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Chip } from "@/ui/Chip";
import { Input } from "@/ui/Field";
import { Filter } from "@/ui/Filter";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { absoluteTime, shortTime } from "@/lib/format";
import { useDebounced } from "@/lib/useDebounced";
import { PACKAGE_LABEL, PACKAGE_TONE } from "@/features/admin/packages";
import { ExportButton } from "@/features/admin/TableActions";
import { useFilters } from "@/features/admin/useFilters";

export const Route = createFileRoute("/_app/admin/organisations")({
  component: OrganisationsScreen,
});

/**
 * Every tenant, searchable.
 *
 * A screen of its own rather than a card on the overview, which is where this
 * list used to live. Two things were wrong with that: the overview is the first
 * thing a staff member opens and it loaded a cross-tenant search nobody had
 * asked for yet, and the list had nowhere to grow — no state filter, no sort,
 * and no room for the columns somebody actually scans for.
 */
function OrganisationsScreen() {
  const filters = useFilters();
  const search = filters.get("q");
  const setSearch = (value: string) => filters.set("q", value);
  const only: "all" | "suspended" = filters.on("suspended") ? "suspended" : "all";
  const setOnly = (value: "all" | "suspended") =>
    filters.toggle("suspended", value === "suspended");
  const debounced = useDebounced(search, 250);

  const organisations = useQuery({
    queryKey: ["platform-organisations", debounced],
    queryFn: ({ signal }) =>
      api.get<{ organisations: AdminOrganisation[] | null }>(
        "/admin/organisations",
        { search: debounced || undefined, limit: 200 },
        signal,
      ),
  });

  const all = organisations.data?.organisations ?? [];
  const suspended = all.filter((o) => o.status === "suspended").length;
  const list = only === "suspended" ? all.filter((o) => o.status === "suspended") : all;

  return (
    <Panel
      /* The filter leads, the search and the export trail — one band rather
         than everything crowded against the right edge with nine hundred
         empty pixels beside it. */
      toolbar={
        <>
          <Filter
            label="All"
            count={all.length}
            active={only === "all"}
            onClick={() => setOnly("all")}
          />
          {/* Only when there is one. A permanent "Suspended 0" trains people
              to ignore the place they would look to find out. */}
          {suspended > 0 && (
            <Filter
              label="Suspended"
              count={suspended}
              tone="down"
              active={only === "suspended"}
              onClick={() => setOnly(only === "suspended" ? "all" : "suspended")}
            />
          )}
        </>
      }
      actions={
        <div className="flex items-center gap-2">
          <Input
            value={search}
            placeholder="Name, slug or owner"
            // Named, not merely hinted: a placeholder is not an accessible
            // name and disappears the moment somebody types.
            aria-label="Search organisations"
            className="h-8 w-64 text-[13px]"
            onChange={(e) => setSearch(e.target.value)}
          />
          <ExportButton
            name="drumlin-organisations"
            rows={list}
            columns={[
              { header: "organisation", value: (o) => o.display_name },
              { header: "slug", value: (o) => o.slug },
              { header: "owner", value: (o) => o.owner_email ?? "" },
              { header: "status", value: (o) => o.status },
              { header: "package", value: (o) => o.package },
              { header: "projects", value: (o) => o.projects },
              { header: "services", value: (o) => o.services },
              { header: "databases", value: (o) => o.databases },
              { header: "created", value: (o) => o.created_at },
            ]}
          />
        </div>
      }
      padded={false}
    >
      {organisations.isLoading ? (
        <div className="px-5 py-4">
          <TableSkeleton rows={8} columns={6} />
        </div>
      ) : organisations.isError ? (
        <div className="px-5 py-4">
          <ErrorState error={organisations.error} onRetry={() => void organisations.refetch()} />
        </div>
      ) : list.length === 0 ? (
        <div className="px-5 py-8">
          <EmptyState
            title="Nothing matches"
            description={
              debounced ? `No organisation matches "${debounced}".` : "There are no organisations."
            }
          />
        </div>
      ) : (
        <div className="px-5 py-3">
          <Table dense fixed>
            <thead>
              <Tr>
                <Th width="w-[26%]">Organisation</Th>
                <Th width="w-[21%]">Owner</Th>
                <Th width="w-[12%]">Package</Th>
                <Th width="w-[9%]" numeric>
                  Projects
                </Th>
                <Th width="w-[9%]" numeric>
                  Services
                </Th>
                <Th width="w-[9%]" numeric>
                  Databases
                </Th>
                <Th width="w-[14%]" align="right">
                  Since
                </Th>
              </Tr>
            </thead>
            <tbody>
              {list.map((org) => (
                <Tr key={org.id}>
                  <Td clip title={org.slug}>
                    <Link
                      to="/admin/$orgId"
                      params={{ orgId: org.id }}
                      className="font-medium text-[var(--text-primary)] hover:text-[var(--primary)]"
                    >
                      {org.display_name}
                    </Link>
                    {org.status === "suspended" && (
                      <Chip tone="down" className="ml-2">
                        Suspended
                      </Chip>
                    )}
                  </Td>
                  {/* An address is the one value here that is routinely longer
                      than its column, so it clips and keeps the exact value on
                      hover rather than wrapping and making this row taller than
                      the next one. */}
                  <Td clip title={org.owner_email || org.slug}>
                    <span className="text-[var(--text-secondary)]">
                      {org.owner_email || "—"}
                    </span>
                  </Td>
                  <Td>
                    <Chip tone={PACKAGE_TONE[org.package]}>{PACKAGE_LABEL[org.package]}</Chip>
                  </Td>
                  <Td numeric>{org.projects}</Td>
                  <Td numeric>{org.services}</Td>
                  <Td numeric>{org.databases}</Td>
                  <Td align="right" title={absoluteTime(org.created_at)}>
                    {shortTime(org.created_at)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Panel>
  );
}
